import {
  CircularProgress,
  Box,
  Container,
  Typography,
  IconButton,
  Paper,
  useTheme,
  useMediaQuery,
} from '@mui/material'
import { useContext, useEffect, useState, useMemo, useCallback } from 'react'
import { useParams, useSearchParams, useNavigate } from 'react-router-dom'
import ConfirmationModal from 'src/consumer/components/helperComponents'
import { UnsecureSetupContext } from 'src/consumer/context/UnsecureSetupContext'
import PrescriptionTemplateForm from 'src/mentor/components/MentorPrescriptionTemplates/components/PrescriptionTemplateForm'
import { styled } from '@mui/material/styles'
import SupportHeader from 'src/supportPortal/layout/SupportHeader'
import { getSPDataWithParams, postSPDataWithParams } from 'src/supportPortal/services/services'
import {
  getDataMapFromCommonResponse,
  isCommonResponseSuccessfulV2,
} from 'src/mentor/helpers/utilityFunctions'
import useSupportPrescriptionTemplateFormStates from './hooks/useSupportPrescriptionTemplateFormStates'
import ArrowBack from '@mui/icons-material/ArrowBack'
import { CustomSelect, selectTypes } from 'src/consumer/components/helperComponents'

const PageOuter = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  height: '100vh',
  overflow: 'hidden',
  backgroundColor: 'var(--support-rx-bg, #f1f5f9)',
})

const PageContainer = styled(Container)(({ theme }) => ({
  width: '100%',
  flex: 1,
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
  padding: theme.spacing(2.5, 2),
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2, 1.5),
  },
}))

const PageMainContentContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  flex: 1,
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
  marginTop: theme.spacing(2),
  borderRadius: 12,
  overflow: 'hidden',
  boxShadow: '0 4px 24px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)',
  border: '1px solid',
  borderColor: theme.palette.divider,
  backgroundColor: theme.palette.background.paper,
  padding: theme.spacing(2),
  [theme.breakpoints.down('sm')]: {
    marginTop: theme.spacing(1.5),
    borderRadius: 10,
    padding: theme.spacing(1.5),
  },
}))

const ClinicSelectWrapper = styled(Box)(({ theme }) => ({
  minWidth: 0,
  flex: '1 1 280px',
  maxWidth: 360,
  '& .MuiInputBase-root': {
    borderRadius: 8,
    backgroundColor: theme.palette.mode === 'light' ? theme.palette.grey[50] : theme.palette.grey[900],
  },
}))

function PrescriptionViewPageHeading({ clinicLocationOptions, associatedClinics, clinicId, mentorSlug }) {
  const navigate = useNavigate()
  const theme = useTheme()
  const isNarrow = useMediaQuery(theme.breakpoints.down('sm'))
  const selectedClinic = associatedClinics?.find((association) => association?.id === clinicId) || null

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', minWidth: 0 }}>
      <IconButton
        onClick={() => {
          navigate(`/support-home/practitioner-management/doctors/${mentorSlug}/prescription-templates`)
        }}
        size="small"
        sx={{
          color: 'inherit',
          '&:hover': { backgroundColor: 'rgba(255,255,255,0.12)' },
        }}
        aria-label="Back to prescription templates"
      >
        <ArrowBack />
      </IconButton>
      {!isNarrow && (
        <Typography
          variant="body2"
          sx={{
            fontWeight: 500,
            opacity: 0.9,
            whiteSpace: 'nowrap',
          }}
        >
          Prescription templates
        </Typography>
      )}
      <Box sx={{ flex: 1, minWidth: 0 }} />
      {!isNarrow && (
        <Typography variant="body2" sx={{ fontWeight: 500, opacity: 0.9, whiteSpace: 'nowrap' }}>
          Clinic
        </Typography>
      )}
      <ClinicSelectWrapper style={{ maxWidth: isNarrow ? '100%' : '360px' }}>
        <CustomSelect
          onChangeEvent={(e) => {
            navigate(
              `/support-home/practitioner-management/doctors/${mentorSlug}/prescription-templates/${e.target.value?.id}`
            )
          }}
          options={clinicLocationOptions}
          value={selectedClinic}
          id="presc-template"
          selectType={selectTypes?.object}
          getOptionLabel={(option) => option?.title || option?.name || '--'}
          getOptionValue={(option) => option?.id || ''}
        />
      </ClinicSelectWrapper>
    </Box>
  )
}

export default function SupportPrescriptionTemplateViewPage() {
  const { mentorSlug, clinicId } = useParams()
  const [searchParams] = useSearchParams()
  const eventId = searchParams.get('eventId')
  const { unSecureSetupData } = useContext(UnsecureSetupContext)
  const [associatedClinics, setAssociatedClinics] = useState([])
  const [mentorProfileData, setMentorProfileData] = useState(null)

  const clinicBrands = useMemo(() => {
    const clinicBrandIdToClinicBrandMap = {}
    associatedClinics?.forEach((association) => {
      if (association?.clinicBrandId) {
        clinicBrandIdToClinicBrandMap[association.clinicBrandId] = {
          id: association.clinicBrandId,
          clinicBrandName: association.clinicBrandName,
        }
      }
    })
    return Object.values(clinicBrandIdToClinicBrandMap)
  }, [associatedClinics])

  const clinicLocationOptions = useMemo(() => {
    return clinicBrands?.map((clinicBrand) => ({
      label: clinicBrand?.clinicBrandName || clinicBrand?.title || '--',
      options: associatedClinics
        ?.filter((association) => association?.clinicBrandId === clinicBrand?.id)
        ?.map((clinic) => ({
          ...clinic,
          title: clinic?.name || clinic?.title || clinic?.clinicName || '--',
        })),
    }))
  }, [associatedClinics, clinicBrands])

  const {
    handlers,
    loading,
    printPreviewType: prescriptionTemplateType,
    setPrintPreviewType: setPrescriptionTemplateType,
    paperPrescriptionTemplate,
    setPaperPrescriptionTemplate,
    pdfPrescriptionTemplate,
    setPdfPrescriptionTemplate,
    pdfOptions,
    paperOptions,
    paperPrintSettings,
    addTemplate,
    deleteTemplate,
    activateTemplate,
    confirmationModalProps,
    handleSubmit,
    savePrescriptionTemplate,
  } = useSupportPrescriptionTemplateFormStates({
    clinicBrands,
    clinicId,
    mentorSlug,
    associatedClinics,
    mentorProfileData,
  })

  const getAssociatedClinics = useCallback(async () => {
    try {
      const resp = await getSPDataWithParams(
        '/api/secure/cc/support/doctor/clinic-associations',
        { 'mentor-slug': mentorSlug },
        undefined,
        undefined,
        true,
      )
      if (resp && isCommonResponseSuccessfulV2(resp)) {
        const dat = getDataMapFromCommonResponse(resp)?.clinic
        setAssociatedClinics(dat || [])
      }
    } catch (error) {
      console.error('Error fetching clinic associations:', error)
      setAssociatedClinics([])
    }
  }, [mentorSlug])

  const getMentorProfile = useCallback(async () => {
    if (!mentorSlug) return
    try {
      const resp = await getSPDataWithParams(
        '/api/secure/cc/support/doctor/by-slug',
        { slug: mentorSlug },
        undefined,
        undefined,
        true,
      )
      if (resp && isCommonResponseSuccessfulV2(resp)) {
        const data = getDataMapFromCommonResponse(resp)?.mentor
        setMentorProfileData(data)
        return data
      }
    } catch (error) {
      console.error('Error fetching mentor profile:', error)
    }
  }, [mentorSlug])

  useEffect(() => {
    if (mentorSlug && !mentorProfileData) {
      // Fetch mentor profile first so we can get the ID for prescription view settings
      getMentorProfile().then((profileData) => {
        if (profileData) {
          getAssociatedClinics()
        }
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mentorSlug])


  return (
    <PageOuter className="support-prescription-print-root">
      <style>
        {`
          @media print {
            * {
              scrollbar-width: none !important;
              -ms-overflow-style: none !important;
            }
            *::-webkit-scrollbar {
              display: none !important;
              width: 0 !important;
              height: 0 !important;
            }
            * {
              box-shadow: none !important;
            }
            body, html {
              overflow: visible !important;
              margin: 0 !important;
              padding: 0 !important;
              height: auto !important;
            }
            .hide-on-print-only {
              display: none !important;
            }
            .support-prescription-print-root {
              overflow: visible !important;
              height: auto !important;
              min-height: 0 !important;
            }
            .support-prescription-print-root .support-prescription-page-container,
            .support-prescription-print-root .support-prescription-main-content {
              flex: none !important;
              height: auto !important;
              min-height: 0 !important;
              overflow: visible !important;
            }
            .actually-printable-prescription {
              position: static !important;
              left: auto !important;
              top: auto !important;
              width: 100% !important;
              height: auto !important;
              visibility: visible !important;
              pointer-events: auto !important;
              overflow: visible !important;
            }
            body.support-prescription-print-active * {
              visibility: hidden !important;
            }
            body.support-prescription-print-active .actually-printable-prescription,
            body.support-prescription-print-active .actually-printable-prescription * {
              visibility: visible !important;
            }
            body.support-prescription-print-active .actually-printable-prescription {
              position: absolute !important;
              left: 0 !important;
              top: 0 !important;
              width: 100% !important;
              height: auto !important;
              overflow: visible !important;
            }
            .actually-printable-prescription [data-print-footer="true"],
            .actually-printable-prescription .footer-spacer-rectangle {
              page-break-inside: avoid !important;
            }
            .actually-printable-prescription [data-print-footer="true"] {
              page-break-before: avoid !important;
            }
          }
        `}
      </style>
      <div className="hide-on-print-only">
        <SupportHeader
          heading={
            <PrescriptionViewPageHeading
              clinicId={clinicId}
              clinicLocationOptions={clinicLocationOptions}
              associatedClinics={associatedClinics}
              mentorSlug={mentorSlug}
            />
          }
          showBackButton={false}
        />
      </div>
      <PageContainer className="support-prescription-page-container">
        {loading && (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2,
              py: 6,
            }}
          >
            <Paper
              elevation={0}
              sx={{
                p: 4,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 2,
                borderRadius: 3,
                border: '1px solid',
                borderColor: 'divider',
                backgroundColor: 'background.paper',
              }}
            >
              <CircularProgress size={40} thickness={4} />
              <Typography variant="body1" color="text.secondary">
                Loading prescription template…
              </Typography>
            </Paper>
          </Box>
        )}
        {!loading && (
          <>
            <PageMainContentContainer className="support-prescription-main-content">
              <PrescriptionTemplateForm
                printPreviewType={prescriptionTemplateType}
                pdfOptions={pdfOptions}
                printOptions={paperOptions}
                setPrintPreviewType={setPrescriptionTemplateType}
                prescriptionTemplate={
                  prescriptionTemplateType === 'print' ? paperPrescriptionTemplate : pdfPrescriptionTemplate
                }
                setPrescriptionTemplate={
                  prescriptionTemplateType === 'print'
                    ? setPaperPrescriptionTemplate
                    : setPdfPrescriptionTemplate
                }
                templateActions={{
                  addTemplate,
                  deleteTemplate,
                  activateTemplate,
                }}
                handlers={handlers}
                customOptions={unSecureSetupData}
                disabled={false}
                isUsingNewPrintMethod={true}
                handleSubmit={handleSubmit}
                eventId={eventId}
                mentorProfileData={mentorProfileData}
                savePrescriptionTemplate={savePrescriptionTemplate}
                clinicId={clinicId}
                pdfPreviewApiFn={postSPDataWithParams}
                pdfPreviewApiEndpoint="/api/secure/cc/support/practitioner/prescription/dummy/generate/pdf/preview"
                pdfPreviewExtraParams={{ 'practitioner-slug': mentorSlug }}
                emrSectionsApiSignature={`/api/secure/cc/support/practitioner/emr-sections${mentorProfileData?.slug || mentorSlug ? `?practitioner-slug=${mentorProfileData?.slug || mentorSlug}` : `?practitioner-id=${mentorProfileData?.id}`}`}
                emrSettingsSaveApiSignature={`/api/secure/cc/support/practitioner/emr-sections/save${mentorProfileData?.slug || mentorSlug ? `?practitioner-slug=${mentorProfileData?.slug || mentorSlug}` : `?practitioner-id=${mentorProfileData?.id}`}`}
              />
            </PageMainContentContainer>
            <ConfirmationModal {...confirmationModalProps} newImplementation />
          </>
        )}
      </PageContainer>
    </PageOuter>
  )
}
